/**
 * Sección reusable para editar `modelOverrides` per-módulo (Fase 33).
 *
 * Renderea un combo por cada task soportada. Los combos se filtran contra el
 * catálogo (`MODEL_CATALOG`) descargado via `/api/admin/routing` — la misma
 * fuente de verdad que el routing global usa.
 */
import { useMemo, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchRoutingConfig,
  type CatalogEntry,
  type ModelOverride,
  type TaskType,
} from '../../api/admin/routing.js';

interface ModelOverridesSectionProps {
  tasks: readonly TaskType[];
  value: Partial<Record<TaskType, ModelOverride>>;
  onChange: (next: Partial<Record<TaskType, ModelOverride>>) => void;
  disabled?: boolean | undefined;
}

export function ModelOverridesSection({
  tasks,
  value,
  onChange,
  disabled,
}: ModelOverridesSectionProps): ReactElement {
  const catalogQuery = useQuery({
    queryKey: ['admin', 'routing'],
    queryFn: fetchRoutingConfig,
    staleTime: 60_000,
  });

  return (
    <section className="config-section">
      <header className="config-section-head">
        <h4 className="config-section-title">Routing override</h4>
        <p className="config-section-sub">
          Pisa el routing global por task para este módulo. Vacío = usar global / default.
        </p>
      </header>
      <div className="config-fields">
        {catalogQuery.isPending ? (
          <div className="config-state">Cargando catálogo…</div>
        ) : catalogQuery.isError ? (
          <div className="config-state config-state-err">
            Catálogo no disponible: {(catalogQuery.error).message}
          </div>
        ) : (
          tasks.map((task) => (
            <ModelOverrideField
              key={task}
              task={task}
              catalog={catalogQuery.data?.catalog ?? []}
              current={value[task] ?? null}
              onChange={(v) => {
                const next: Partial<Record<TaskType, ModelOverride>> = { ...value };
                if (v === null) delete next[task];
                else next[task] = v;
                onChange(next);
              }}
              disabled={disabled}
            />
          ))
        )}
      </div>
    </section>
  );
}

interface ModelOverrideFieldProps {
  task: TaskType;
  catalog: readonly CatalogEntry[];
  current: ModelOverride | null;
  onChange: (next: ModelOverride | null) => void;
  disabled?: boolean | undefined;
}

function ModelOverrideField({
  task,
  catalog,
  current,
  onChange,
  disabled,
}: ModelOverrideFieldProps): ReactElement {
  const candidates = useMemo(
    () => catalog.filter((e) => e.supports.includes(task) && e.status !== 'deprecated'),
    [catalog, task],
  );
  const overrideEntry = useMemo(
    () =>
      current
        ? catalog.find((e) => e.id === current.model && e.provider === current.provider) ?? null
        : null,
    [catalog, current],
  );
  const overrideKey = current ? `${current.provider}:${current.model}` : '';

  const handleChange = (value: string): void => {
    if (!value) {
      onChange(null);
      return;
    }
    const [provider, ...rest] = value.split(':');
    const model = rest.join(':');
    if (!provider || !model) return;
    onChange({ provider, model });
  };

  return (
    <div className="config-field">
      <label className="config-field-label">
        <code>{task}</code>
      </label>
      <select
        className="config-input"
        value={overrideKey}
        onChange={(e) => handleChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">— (usar global)</option>
        {candidates.map((entry) => (
          <option key={`${entry.provider}:${entry.id}`} value={`${entry.provider}:${entry.id}`}>
            {entry.displayName}
            {entry.status === 'experimental' ? ' ⚠' : ''}
            {' · '}${entry.pricing.inputPer1M}/${entry.pricing.outputPer1M} per 1M
          </option>
        ))}
      </select>
      {overrideEntry?.status === 'experimental' && (
        <p className="config-field-hint">
          ⚠ Experimental · {overrideEntry.notes ?? 'validar antes en producción'}
        </p>
      )}
    </div>
  );
}
